/////////////////////////////////////////////////////////////////////////////////////////////////////////
// 화일명 : AgGridDateCellEditor.tsx
// 설명   : AG-Grid 그리드 셀용 언어별 날짜 팝업 편집기
//   - AG-Grid 내장 agDateStringCellEditor는 브라우저 네이티브 <input type="date">를 사용해서
//     팝업(달력)의 언어가 브라우저/OS 로케일을 따라가 버리고 앱 언어를 반영할 수 없음.
//   - 조건부(검색조건) 영역에서 이미 쓰고 있는 MUI DatePicker + useDatePickerLocale을 그대로 재사용해서,
//     AG-Grid가 요구하는 커스텀 셀 에디터 규격(CustomCellEditorProps)으로 한 번 감싼 어댑터.
//   - 날짜+시간 타입('yyyy-mm-dd hh:mm')은 MUI DateTimePicker(24시간)로 편집 - 날짜/시간을 고른 뒤 [확인]을 눌러야 반영되고
//     (바깥 클릭/취소는 원래 값 유지), [지우기]로 비울 수 있음. 값은 'YYYY-MM-DD HH:mm'으로 전달.
//   - 2026-10-01 : 항상 데스크톱 팝업(desktopModeMediaQuery), 년월/년 형식은 월/년부터 열림(openTo),
//     날짜도 마지막 단위(일/월/년)를 고를 때(onAccept) 확정 - 년월 형식에서 년만 골라도 닫히던 문제 수정,
//     달력을 body로 포탈 + ag-custom-component-popup(그리드 영역에 잘리던 문제 수정)
//   - 값은 항상 ISO('YYYY-MM-DD')로 onValueChange에 전달 - 실제 저장 형식 변환은
//     commonUtils.tsx의 dateColDef()가 붙이는 valueSetter가 담당(관심사 분리).
//   - 사용법: 직접 쓰지 말고 commonUtils.tsx의 dateColDef(language, type)를 통해서만 사용.
// 작성일 : 2026-09-12
/////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { useState, useCallback } from 'react';
import type { CustomCellEditorProps } from 'ag-grid-react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs, { Dayjs } from 'dayjs';
import { useDatePickerLocale } from '../utils/useDatePickerLocale';
import type { DateColDefType } from '../utils/commonUtils';

interface AgGridDateCellEditorProps extends CustomCellEditorProps<any, string> {
  dateType?: DateColDefType;
}

// 날짜 타입 -> DatePicker가 보여줄 선택 단위(년/월/일). 'yyyymmdd'류는 저장만 압축형일 뿐 편집은 전체 날짜로 함.
const VIEWS_BY_TYPE: Record<DateColDefType, Array<'year' | 'month' | 'day'>> = {
  'yyyy-mm-dd': ['year', 'month', 'day'],
  yyyymmdd: ['year', 'month', 'day'],
  'yyyy-mm': ['year', 'month'],
  yyyymm: ['year', 'month'],
  yyyy: ['year'],
  'yyyy-mm-dd hh:mm': ['year', 'month', 'day'], // 날짜+시간은 아래 DateTimePicker 분기에서 따로 처리
};

export default function AgGridDateCellEditor(props: AgGridDateCellEditorProps) {
  const { adapterLocale, localeText, dateFormat, monthFormat } = useDatePickerLocale();
  const [open, setOpen] = useState(true); // 편집 시작하면 바로 달력 팝업이 뜨도록

  const type: DateColDefType = props.dateType || 'yyyy-mm-dd';
  const views = VIEWS_BY_TYPE[type] || VIEWS_BY_TYPE['yyyy-mm-dd'];
  const format = views.length === 1 ? 'YYYY' : views.length === 2 ? monthFormat : dateFormat;
  // 처음 열리는 선택 단위 - 년월 형식은 월부터, 년 형식은 년부터(조건부/폼의 DateField와 동일). 일자 형식은 기본(일)
  const openTo = views.length === 1 ? 'year' : views.length === 2 ? 'month' : undefined;

  const currentValue: Dayjs | null = props.value ? dayjs(props.value) : null;
  const isDateTime = type === 'yyyy-mm-dd hh:mm';
  // 편집 중 임시값 - 확정(onAccept) 전까지 그리드 값에는 반영하지 않음.
  //   (2026-10-01) 날짜도 onChange에서 바로 확정하면, 년월 형식에서 "년"만 골라도 월을 고르기 전에 닫히고 저장돼버렸음 -
  //   고르는 동안은 draft에만 담고 마지막 단위(일/월/년)를 고를 때(onAccept) 확정. 날짜+시간은 값이 비어 있으면 현재 일시로 시작.
  const [draft, setDraft] = useState<Dayjs | null>(currentValue ?? (isDateTime ? dayjs().second(0).millisecond(0) : null));

  const finish = useCallback(() => {
    setOpen(false);
    props.stopEditing?.();
  }, [props]);

  const handleDateAccept = useCallback((newValue: Dayjs | null) => {
    props.onValueChange(newValue && newValue.isValid() ? newValue.format('YYYY-MM-DD') : '');
    finish();
  }, [props, finish]);

  // 날짜+시간: 값을 고르는 동안(onChange)에는 반영하지 않고 [확인](onAccept)에서만 확정 - 시간을 고르기도 전에 편집이 끝나버리지 않게 함.
  // [지우기]는 값을 비운 채로 확정. 확인 없이 닫으면(바깥 클릭/취소) 원래 값 유지.
  const handleAccept = useCallback((newValue: Dayjs | null) => {
    props.onValueChange(newValue && newValue.isValid() ? newValue.format('YYYY-MM-DD HH:mm') : '');
    finish();
  }, [props, finish]);

  if (isDateTime) {
    return (
      <div style={{ background: '#fff', display: 'inline-block' }}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale} localeText={localeText}>
          <DateTimePicker
            open={open}
            onOpen={() => setOpen(true)}
            onClose={finish}
            value={draft}
            onChange={(v: Dayjs | null) => setDraft(v)}
            onAccept={handleAccept}
            desktopModeMediaQuery="@media all"   // 터치 기기에서도 모바일 대화상자 대신 데스크톱 팝업(그리드 안에 붙어야 셀 편집이 유지됨)
            ampm={false}
            format={`${dateFormat} HH:mm`}
            slotProps={{
              textField: { size: 'small', sx: { width: 200, bgcolor: '#fff', '& input': { fontSize: '0.85rem', paddingRight: 0 } }, autoFocus: true },
              actionBar: { actions: ['clear', 'cancel', 'accept'] },
              // 달력+시간 목록은 폭이 커서 입력칸의 오른쪽 끝에 맞춰 왼쪽으로 펼치고(bottom-end), 화면 밖으로 나가지 않게 밀어넣음(preventOverflow)
              // 달력은 body로 포탈(그리드/패널 영역에 잘리지 않게) + ag-custom-component-popup 클래스로 AG-Grid가 '편집기 안' 클릭으로 인식(편집 유지)
              popper: {
                className: 'ag-custom-component-popup',
                placement: 'bottom-end',
                modifiers: [{ name: 'preventOverflow', options: { mainAxis: true, altAxis: false, padding: 4, tether: false } }],
                sx: { '& .MuiPaper-root': { bgcolor: '#fff' } },
              },
            }}
          />
        </LocalizationProvider>
      </div>
    );
  }

  return (
    // MUI TextField(outlined)는 배경이 기본 투명이라 뒤에 있는 원본 셀 값이 비쳐 보임(겹쳐보이는 원인) -
    // 불투명 배경을 명시적으로 깔아서 셀을 완전히 덮음.
    <div style={{ background: '#fff', display: 'inline-block' }}>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale} localeText={localeText}>
        <DatePicker
          open={open}
          onOpen={() => setOpen(true)}
          onClose={finish}
          value={draft}
          onChange={(v: Dayjs | null) => setDraft(v)}
          onAccept={handleDateAccept}
          // 터치 기기(pointer: coarse)면 MUI가 모바일 대화상자("날짜 선택하기")를 띄우는데, 그리드 밖(body)에 그려져서
          // 셀 편집이 끊기고 모양도 다름 - 그리드 편집기는 항상 데스크톱 팝업으로 고정(2026-10-01)
          desktopModeMediaQuery="@media all"
          views={views}
          openTo={openTo}
          format={format}
          slotProps={{
            textField: { size: 'small', sx: { width: 150, bgcolor: '#fff' }, autoFocus: true },
            // 달력은 document.body로 포탈(기본)해서 그리드/분할 패널 영역에 잘리지 않게 함(2026-10-01, 이전엔 disablePortal로
            // 그리드 안에 그려서 높이가 낮은 그리드에서 잘렸음). body에 그려진 달력을 AG-Grid가 '셀 바깥 클릭'(편집취소)으로
            // 처리하지 않도록 AG-Grid 규칙 클래스 ag-custom-component-popup을 붙임.
            popper: { className: 'ag-custom-component-popup', sx: { '& .MuiPaper-root': { bgcolor: '#fff' } } },
          }}
        />
      </LocalizationProvider>
    </div>
  );
}
